import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import Layout from "@/components/layout";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import type {
  AgentPulseLeaderboardEntry,
  AgentPulseWonLostOpportunity,
  GetAgentPulseLiveCountsResponse,
} from "@forgepoint/shared";
import { useAgentPulseStore } from "../agentPulseStore";
import { WeeklyJournal } from "../components/WeeklyJournal";
import { UserLeaderboard } from "../components/UserLeaderboard";
import { SentQuoteViews } from "../components/SentQuoteViews";
import { WinsTable } from "../components/WinsTable";
import { LiveTicker } from "../components/LiveTicker";
import type { TickerItem } from "../types";

/**
 * Main controller for the Agent Pulse dashboard. Reads data from the
 * agentPulseStore and orchestrates the hero memo, leaderboard, sent quote
 * views, wins table, and ticker.
 * @returns The complete Agent Pulse page layout
 */
export default function AgentPulseController() {
  const latestUpdate = useAgentPulseStore((s) => s.latestUpdate);
  const historicalUpdates = useAgentPulseStore((s) => s.historicalUpdates);
  const leaderboardEntries = useAgentPulseStore((s) => s.leaderboardEntries);
  const wonLostOpportunities = useAgentPulseStore(
    (s) => s.wonLostOpportunities,
  );
  const viewsSummary = useAgentPulseStore((s) => s.viewsSummary);
  const liveCounts = useAgentPulseStore((s) => s.liveCounts);
  const isLoading = useAgentPulseStore((s) => s.isLoading);
  const startPolling = useAgentPulseStore((s) => s.startPolling);
  const stopPolling = useAgentPulseStore((s) => s.stopPolling);
  const selectedPulseId = useAgentPulseStore((s) => s.selectedPulseId);
  const selectPulse = useAgentPulseStore((s) => s.selectPulse);

  const agentName = useStandardAgentDisplayName();

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const selectedUpdate = useMemo(
    () =>
      (selectedPulseId
        ? historicalUpdates.find((u) => u.id === selectedPulseId)
        : null) ?? latestUpdate,
    [selectedPulseId, historicalUpdates, latestUpdate],
  );

  const isViewingLatestWeek =
    !selectedPulseId || selectedUpdate?.id === latestUpdate?.id;

  const tickerItems = useMemo(
    () =>
      deriveTickerItems(
        wonLostOpportunities,
        leaderboardEntries,
        liveCounts,
        isViewingLatestWeek,
      ),
    [wonLostOpportunities, leaderboardEntries, liveCounts, isViewingLatestWeek],
  );

  return (
    <Layout breadcrumbs={[{ label: "Agent Pulse", current: true }]}>
      <LiveTicker items={tickerItems} />
      <div className="p-4 pb-14 sm:p-6 lg:px-7 lg:py-6">
        {/* Page header */}
        <div className="mb-5 flex items-end justify-between">
          <div className="flex items-end gap-3.5">
            <h1 className="text-[22px] font-semibold tracking-tight">
              Agent Pulse
            </h1>
            <span className="text-[13.5px] text-muted-foreground">
              {agentName}&apos;s activity.
            </span>
          </div>
          {latestUpdate && (
            <span className="text-[13px] text-muted-foreground">
              Last Pulse at{" "}
              {format(
                new Date(
                  latestUpdate.weekEndDate.getUTCFullYear(),
                  latestUpdate.weekEndDate.getUTCMonth(),
                  latestUpdate.weekEndDate.getUTCDate(),
                  12,
                ),
                "MMM d, yyyy",
              )}
            </span>
          )}
        </div>

        {/* Main grid: hero memo + sidebar */}
        <div className="mt-4 grid items-start gap-6 lg:grid-cols-[1fr_360px]">
          <WeeklyJournal
            latestUpdate={latestUpdate}
            historicalUpdates={historicalUpdates}
            selectedUpdateId={selectedPulseId}
            onSelectUpdate={selectPulse}
            isLoading={isLoading}
          />

          {/* Sidebar */}
          <div className="flex flex-col gap-6 lg:top-6">
            <UserLeaderboard
              entries={leaderboardEntries}
              lastWeekLeader={
                latestUpdate?.dataSnapshot?.topMessageUserName
                  ? {
                      userName: latestUpdate.dataSnapshot.topMessageUserName,
                      messageCount:
                        latestUpdate.dataSnapshot.topMessageCount ?? 0,
                    }
                  : null
              }
              isLoading={isLoading}
            />
            <SentQuoteViews
              viewsSummary={viewsSummary}
              pulseSnapshot={latestUpdate?.dataSnapshot ?? null}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Won/Lost opportunities — full width */}
        <div className="mt-6">
          <WinsTable
            opportunities={wonLostOpportunities}
            pulseSnapshot={selectedUpdate?.dataSnapshot ?? null}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Layout>
  );
}

/**
 * Derives ticker items from won opportunities, leaderboard highlights,
 * live counts, and update availability. Leaderboard and live-count items are
 * only current-week data, so they are omitted while a historical week is
 * selected and the ticker shows that week's won/lost deals alone.
 * @param wonLostOpportunities - Won/lost opportunities for the selected week
 * @param leaderboardEntries - User leaderboard entries
 * @param liveCounts - Live counts of activity since the last pulse update
 * @param isViewingLatestWeek - Whether the latest week is currently selected
 * @returns Array of ticker items for the scrolling bar
 */
function deriveTickerItems(
  wonLostOpportunities: AgentPulseWonLostOpportunity[],
  leaderboardEntries: AgentPulseLeaderboardEntry[],
  liveCounts: GetAgentPulseLiveCountsResponse | null,
  isViewingLatestWeek: boolean,
): TickerItem[] {
  // Build items by category so we can interleave them
  const oppItems: TickerItem[] = [];

  // Won opportunity highlights
  for (const opp of wonLostOpportunities.filter(
    (o) => o.status === "ACCEPTED",
  )) {
    const amount = opp.amount
      ? formatTickerCurrency(parseFloat(opp.amount))
      : "";
    const via = opp.salesRepName ? ` via ${opp.salesRepName}` : "";
    oppItems.push({
      tag: { text: "WON", variant: "work" },
      text: `${opp.name}${amount ? ` - ${amount}` : ""}${via}`,
    });
  }

  // Lost opportunity highlights
  for (const opp of wonLostOpportunities.filter((o) => o.status === "LOST")) {
    const amount = opp.amount
      ? formatTickerCurrency(parseFloat(opp.amount))
      : "";
    const via = opp.salesRepName ? ` via ${opp.salesRepName}` : "";
    oppItems.push({
      tag: { text: "LOST", variant: "lost" },
      text: `${opp.name}${amount ? ` - ${amount}` : ""}${via}`,
    });
  }

  if (!isViewingLatestWeek) {
    return oppItems;
  }

  const statsItems: TickerItem[] = [];

  // Live counts since last update
  if (liveCounts) {
    if (liveCounts.opportunities > 0) {
      statsItems.push({
        bold: `+${liveCounts.opportunities}`,
        text: "new opportunities since last pulse",
      });
    }
    if (liveCounts.quotes > 0) {
      statsItems.push({
        bold: `+${liveCounts.quotes}`,
        text: "new quotes since last pulse",
      });
    }
  }

  if (leaderboardEntries.length > 0) {
    const topUser = leaderboardEntries[0];
    statsItems.push({
      bold: topUser.userName,
      text: `leading with ${topUser.messageCount} messages`,
    });
  }

  const totalMessages = leaderboardEntries.reduce(
    (sum, entry) => sum + entry.messageCount,
    0,
  );
  if (totalMessages > 0) {
    statsItems.push({
      bold: `${totalMessages}`,
      text: "total team messages this week",
    });
  }

  return interleave(oppItems, statsItems);
}

/**
 * Round-robin interleaves two arrays so items of the same type
 * don't cluster together in the ticker.
 * @param a - First array of ticker items
 * @param b - Second array of ticker items
 * @returns A single array with items dispersed evenly
 */
function interleave(a: TickerItem[], b: TickerItem[]): TickerItem[] {
  const result: TickerItem[] = [];
  const maxLen = Math.max(a.length, b.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < a.length) {
      result.push(a[i]);
    }
    if (i < b.length) {
      result.push(b[i]);
    }
  }
  return result;
}

/**
 * Formats a number as compact USD for ticker display.
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g. "$1.86M")
 */
function formatTickerCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return `$${value.toLocaleString()}`;
}
